import { Injectable, computed, inject, signal } from '@angular/core';
import { App } from '@capacitor/app';
import { PlatformService } from './platform.service';
import { RemoteConfigService } from './remote-config.service';
import { LoggingService } from './logging.service';

export interface ForceUpdateCheckResult {
  updateRequired: boolean;
  currentVersion: string;
  minimumVersion: string;
  title: string;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class ForceUpdateService {
  private readonly remoteConfigService = inject(RemoteConfigService);
  private readonly platformService = inject(PlatformService);
  private readonly loggingService = inject(LoggingService);

  readonly isChecking = signal(false);

  readonly checkResult = signal<ForceUpdateCheckResult | null>(null);

  readonly updateRequired = computed(() => this.checkResult()?.updateRequired ?? false);

  async checkForUpdate(): Promise<ForceUpdateCheckResult> {
    if (!this.platformService.isNativeMobile()) {
      const result: ForceUpdateCheckResult = {
        updateRequired: false,
        currentVersion: 'web',
        minimumVersion: 'N/A',
        title: '',
        message: '',
      };
      this.checkResult.set(result);
      return result;
    }

    this.isChecking.set(true);

    try {
      const appInfo = await App.getInfo();
      const currentVersion = appInfo.version;

      const config = this.remoteConfigService.config();

      const minimumVersion = this.platformService.isIOS()
        ? config.min_app_version_ios
        : config.min_app_version_android;

      const updateRequired =
        config.force_update_enabled && this.isVersionLessThan(currentVersion, minimumVersion);

      const result: ForceUpdateCheckResult = {
        updateRequired,
        currentVersion,
        minimumVersion,
        title: config.force_update_title,
        message: config.force_update_message,
      };

      this.checkResult.set(result);

      return result;
    } catch (err) {
      this.loggingService.logException(err, 'ForceUpdateService: Check failed');

      const result: ForceUpdateCheckResult = {
        updateRequired: false,
        currentVersion: 'unknown',
        minimumVersion: 'unknown',
        title: '',
        message: '',
      };
      this.checkResult.set(result);
      return result;
    } finally {
      this.isChecking.set(false);
    }
  }

  async openAppStore(): Promise<void> {
    const iosAppStoreUrl = 'https://apps.apple.com/app/loandepot/id6738976507';
    const androidPlayStoreUrl = 'https://play.google.com/store/apps/details?id=com.loandepot.loandepotmobile';

    const url = this.platformService.isIOS() ? iosAppStoreUrl : androidPlayStoreUrl;

    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url });
    } catch (err) {
      this.loggingService.logException(err, 'ForceUpdateService: Failed to open app store');
      window.open(url, '_blank');
    }
  }

  private isVersionLessThan(version1: string, version2: string): boolean {
    const v1Parts = this.parseVersion(version1);
    const v2Parts = this.parseVersion(version2);

    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
      const v1Part = v1Parts[i] || 0;
      const v2Part = v2Parts[i] || 0;

      if (v1Part < v2Part) {
        return true;
      }
      if (v1Part > v2Part) {
        return false;
      }
    }

    return false;
  }

  private parseVersion(version: string): number[] {
    const cleanVersion = version.split('-')[0];

    return cleanVersion
      .split('.')
      .map(part => parseInt(part, 10))
      .filter(num => !isNaN(num));
  }
}
